import { type ReactNode } from 'react';

import {
  Dialog,
  DialogContent as DialogBody,
  DialogTitle as DialogHeader,
  IconButton,
  Typography as Text,
} from '@material-ui/core';

import { RiCloseLine } from '@remixicon/react';

import { Link } from '@spotify-internal/encore-web';
import {
  DateUtils,
  ResourceNameLink,
  StringUtils,
} from '@spotify/confidence-core-react';
import { isSafeUrl } from '@spotify/plugin-confidence-artifacts-react';
import { ObservationRefFragment } from '@spotify/plugin-confidence-issues-react';

import styles from './ObservationDetailsDialog.module.css';

export type ObservationDetailsDialogProps = {
  observation: ObservationRefFragment | null;
  onClose: () => void;
};

const formatDateTime = (time: string): string =>
  DateUtils.toDateString(time, DateUtils.SHORT_DATE_TIME_FORMAT) ?? time;

const DetailItem = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className={styles.detailItem}>
    <Text variant="body2" color="textSecondary">
      {label}
    </Text>
    <Text variant="body2" className={styles.detailValue}>
      {value}
    </Text>
  </div>
);

const UriValue = ({ uri }: { uri: string }) =>
  isSafeUrl(uri) ? (
    <Link href={uri} target="_blank" rel="noopener noreferrer" data-track-source-anchor="4f2b80efe1;uri-value-link">
      {uri}
    </Link>
  ) : (
    <span className={styles.resourceReference}>
      <ResourceNameLink name={uri} text={uri} withIcon  data-track-source-anchor="0b3b34879d;uri-value-resource-name-link"/>
    </span>
  );

export const ObservationDetailsDialog = ({
  observation,
  onClose,
}: ObservationDetailsDialogProps) => (
  <Dialog
    open={observation !== null}
    onClose={() => onClose()}
    maxWidth="sm"
    fullWidth
   data-track-source-anchor="5837ea5171">
    <DialogHeader>
      Observation details
      <IconButton
        aria-label="Close"
        onClick={() => onClose()}
        style={{ position: 'absolute', right: 8, top: 8 }}
       data-track-source-anchor="bb3920d06f;;low">
        <RiCloseLine size={16} />
      </IconButton>
    </DialogHeader>
    {observation && (
      <DialogBody>
        <div className={styles.details}>
          <DetailItem label="Summary" value={observation.summary} />
          {observation.excerpt && (
            <DetailItem label="Excerpt" value={observation.excerpt} />
          )}
          <DetailItem
            label="Kind"
            value={StringUtils.humanize(observation.kind)}
          />
          <DetailItem label="Source" value={observation.source.system} />
          <DetailItem
            label="Observed"
            value={formatDateTime(observation.eventTime)}
          />
          <DetailItem
            label="Created"
            value={formatDateTime(observation.createTime)}
          />
          {observation.source.externalId && (
            <DetailItem
              label="External ID"
              value={
                <span className={styles.resourceReference}>
                  <ResourceNameLink
                    name={observation.source.externalId}
                    text={observation.source.externalId}
                    withIcon
                   data-track-source-anchor="f1360442b0;observation-details-dialog-resource-name-link"/>
                </span>
              }
            />
          )}
          {observation.source.uri && (
            <DetailItem
              label="URI"
              value={<UriValue uri={observation.source.uri} />}
            />
          )}
        </div>
      </DialogBody>
    )}
  </Dialog>
);
